import { Button, ExpandableSection, Text, Title } from '@patternfly/react-core';
import { useState } from 'react';
import { CategoryQuestion } from '../../types/discover';
import ChartView from '../../components/charts/ChartView';
import { InsightsActionsResponse } from '../../api/types';
import InsightsActions from '../../components/data-builder/InsightsActions';
import Loader from '../../components/util/Loader';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { useMount } from 'react-use';
import { ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { useApplication } from '../../components/user/ApplicationProvider';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faComments, faSparkle } from '@fortawesome/pro-solid-svg-icons';
import { storeDiscoverChat } from '../../helpers/discover-chat-store';

type Props = {
	question: CategoryQuestion;
	selectedDate?: TNewDateRange;
};

const Question = (props: Props) => {
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [insightsActions, setInsightsActions] = useState<InsightsActionsResponse | undefined>();
	const [fetchInsightsActions, setFetchInsightsActions] = useState<boolean>(false);
	const [showInsightsActions, setShowInsightsActions] = useState<boolean>(false);
	const [insightsActionsLoading, setInsightsActionsLoading] = useState<boolean>(false);
	const { question, selectedDate } = props;
	const { zoneSettings } = useApplication();
	const [isInsightsDisabled, setIsInsightsDisabled] = useState(false);
	const navigator = useNavigate();

	useMount(() => {
		const disableZoneSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.AI_disabled
		);

		if (disableZoneSetting && disableZoneSetting.enabled) {
			setIsInsightsDisabled(true);
		}
	});

	const onToggle = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<ExpandableSection
			toggleText={question.question}
			onToggle={onToggle}
			isExpanded={isExpanded}
			isIndented
			data-testid={`question-${question.key}`}
		>
			{question.title && <Title headingLevel="h2">{question.title}</Title>}
			{question.description && <Text>{question.description}</Text>}
			{question.hasChart && (
				<div className={'chart-data'}>
					<div className={'chart-section'}>
						<ChartView
							chartId={question.chartId ?? '0'}
							selectedDate={selectedDate}
							allowClickNavigate
							transparentBackground={true}
							fetchInsights={fetchInsightsActions}
							setInsightsActions={(e) => {
								setInsightsActions(e);
								setInsightsActionsLoading(!insightsActionsLoading);
							}}
							setHasRetrievalData={setShowInsightsActions}
						/>
					</div>

					{showInsightsActions && !isInsightsDisabled && (
						<div className={'insights-section'}>
							{!insightsActions && !insightsActionsLoading && (
								<Button
									key="generate-insights"
									variant="primary"
									className={'generate-insights-btn'}
									onClick={() => {
										setFetchInsightsActions(true);
										setInsightsActionsLoading(true);
									}}
								>
									<FontAwesomeIcon icon={faSparkle} /> AI * Insights
								</Button>
							)}

							{insightsActionsLoading && <Loader />}

							{insightsActions && !insightsActionsLoading && (
								<>
									<InsightsActions
										insights={insightsActions}
										hasBackground={true}
									/>
									{question.chartId && (
										<Button
											variant="link"
											className="discover-chat-btn"
											onClick={() => {
												const nonce = storeDiscoverChat({
													insights: insightsActions,
													questionText: question.question,
												});
												navigator(
													`/analyze/charts/view/chart/${question.chartId!}?discover=${nonce}`
												);
											}}
										>
											<FontAwesomeIcon icon={faComments} /> Chat about this
										</Button>
									)}
								</>
							)}
						</div>
					)}
				</div>
			)}

			{props.question.dashboardId && (
				<>
					<br />
					<div className="text-center">
						<label>This question is linked to a Dashboard</label>
						<br />
						<Button
							key="navigate-dashboard"
							variant="primary"
							className={'generate-insights-btn'}
							onClick={() => {
								const chatParam = encodeURIComponent(props.question.question);
								navigator(
									// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
									`/analyze/dashboards/view/${props.question.dashboardId}#chat=${chatParam}`
								);
							}}
						>
							<FontAwesomeIcon icon={faArrowRight} /> Go To Dashboard
						</Button>
						<br />
					</div>
				</>
			)}
		</ExpandableSection>
	);
};

export default Question;
